import type { RefObject } from 'react';
import React, { useEffect, useMemo, useState } from 'react';
import * as S from 'containers/PropertyPanel/PropertyPanel.styles';
import { SwitchWithLabel } from 'components/SwitchWithLabel';
import { Radio } from '@mui/material';
import { PSD_CALC_MODE } from '../types';
import { LineChart } from 'components/LineChart';
import { prepareGraphData } from '../util';
import type { PortInterfaceWithName } from 'store/units/types';
import { convertToCurrentMetric, CONVERSIONS } from 'util/conversions';
import { useSelector } from 'react-redux';
import type { RootState } from 'store';
import type { ChartDataset } from 'chart.js/auto';

export interface FlowCurvePointInterface {
  x: number;
  y: number;
  interpolated?: boolean;
}

export interface DatasetInterface extends Partial<
  ChartDataset<'line', FlowCurvePointInterface[]>
> {
  data?: FlowCurvePointInterface[];
  borderColor?: string;
  backgroundColor?: string;
  lineTension?: number;
  cubicInterpolationMode?: 'default' | 'monotone';
}

interface FlowCurveInterface {
  inputPorts: PortInterfaceWithName[] | undefined;
  isStorage?: boolean;
  activePortIndex: number;
  outputPorts: PortInterfaceWithName[] | undefined;
  activeDatasetIndex: number;
  pxxPoints: FlowCurvePointInterface[] | undefined;
  upperLimit: number | undefined;
  lowerLimit: number | undefined;
  psdCalcMode: string;
  canvasRef: RefObject<HTMLCanvasElement | null>;
  ticks?: number[];
  setLimits: (lower: number, upper: number) => void;
  setPsdCalcMode: (type: PSD_CALC_MODE) => void;
}

export const FlowCurve = ({
  activePortIndex,
  isStorage,
  outputPorts,
  inputPorts,
  activeDatasetIndex,
  pxxPoints,
  canvasRef,
  lowerLimit,
  upperLimit,
  psdCalcMode,
  setLimits,
  setPsdCalcMode,
  ticks,
}: FlowCurveInterface) => {
  const [datasets, setDatasets] = useState<DatasetInterface[]>();
  const [showGridLines, setShowGridLines] = useState(true);
  const [showFeed, setShowFeed] = useState(false);
  const metricType = useSelector(
    (state: RootState) => state.settingsState.metricType,
  );

  useEffect(() => {
    const {
      datasets: newDatasets,
      lowerLimit,
      upperLimit,
    } = prepareGraphData(
      outputPorts,
      inputPorts,
      '',
      psdCalcMode,
      activePortIndex,
      undefined,
      {
        isStorage,
        showFeed,
      },
    );

    setLimits(lowerLimit, upperLimit);
    setDatasets(newDatasets);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    inputPorts,
    outputPorts,
    psdCalcMode,
    showFeed,
    activePortIndex,
    isStorage,
  ]);

  const convertedPxxPoints = useMemo(() => {
    return pxxPoints?.map((point) => {
      const newX = convertToCurrentMetric({
        value: point.x,
        metricType,
        conversionType: CONVERSIONS.MILLIMETER,
      });
      if (typeof newX !== 'undefined') {
        const convertedPoint: FlowCurvePointInterface = {
          x: newX,
          y: point.y,
          interpolated: point.interpolated,
        };
        return convertedPoint;
      } else {
        return point;
      }
    });
  }, [pxxPoints, metricType]);

  const convertedTicks = useMemo(() => {
    return metricType.value === 'IMPERIAL'
      ? ticks?.map((tick) => {
          const newTick = convertToCurrentMetric({
            value: tick,
            metricType,
            conversionType: CONVERSIONS.MILLIMETER,
          });
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
          return newTick || tick;
        })
      : ticks;
  }, [ticks, metricType]);

  return (
    <>
      <div
        id='chart-container'
        style={{
          position: 'relative',
          height: `calc(100vh - 140px)`,
          maxWidth: '100%',
          width: '1400px',
          flex: '0 0 auto',
        }}
      >
        <canvas ref={canvasRef} />
        {datasets && (
          <LineChart
            canvasRef={canvasRef}
            datasets={datasets}
            pxxPoints={convertedPxxPoints}
            lowerLimit={lowerLimit}
            upperLimit={upperLimit}
            showGridLines={showGridLines}
            psdCalcMode={psdCalcMode}
            metricType={metricType}
            setLimits={setLimits}
            activeDatasetIndex={activeDatasetIndex}
            ticks={convertedTicks ?? ticks}
            enableWheelZoom={false}
            enablePan={false}
          />
        )}
      </div>
      <S.CurveMenu>
        <S.CurveSwitchWrapper>
          <SwitchWithLabel
            value={showGridLines}
            setValue={(val) => setShowGridLines(val)}
            label={`Show grid`}
            size={150}
          />
          {!isStorage && (
            <SwitchWithLabel
              value={showFeed}
              setValue={(val) => setShowFeed(val)}
              label={`Show feed`}
              size={150}
            />
          )}
        </S.CurveSwitchWrapper>
        <S.CurveRadioContainer>
          <S.CurveRadioWrapper>
            <Radio
              checked={psdCalcMode === PSD_CALC_MODE.MASS}
              onChange={() => setPsdCalcMode(PSD_CALC_MODE.MASS)}
              color='info'
            />
            <>{'Mass [mtph]'}</>
          </S.CurveRadioWrapper>
          <S.CurveRadioWrapper>
            <Radio
              checked={psdCalcMode === PSD_CALC_MODE.PERCENT}
              onChange={() => setPsdCalcMode(PSD_CALC_MODE.PERCENT)}
              color='info'
            />
            <>{'Percent [%]'}</>
          </S.CurveRadioWrapper>
        </S.CurveRadioContainer>
      </S.CurveMenu>
    </>
  );
};
